import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { ButtonComponent } from '../../../buttons/button/button.component';
import { DialogHeaderComponent } from '../../../dialog/dialog-header/dialog-header.component';
import { MatDialogRef } from '@angular/material/dialog';
import { ButtonType } from '../../../enum/button-type.enum';
import { TableColumn } from '../../models/table-column';
import { Observable } from 'rxjs';
import { indicateSignal } from '../../../../extensions/observable.extensions';
import { CsvUtils } from '../../../../utils/csv-utils';
import { ToastrService } from 'ngx-toastr';
import { CheckboxFormFieldComponent } from '../../../form/form-fields/checkbox-form-field/checkbox-form-field.component';
import { SelectAllCheckboxFormFieldComponent } from '../../../form/form-fields/select-all-checkbox-form-field/select-all-checkbox-form-field.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ErrorService } from '../../../../../core';

@Component({
  selector: 'lib-export-csv',
  imports: [
    ButtonComponent,
    DialogHeaderComponent,
    CheckboxFormFieldComponent,
    SelectAllCheckboxFormFieldComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './export-csv.component.html',
  styleUrl: './export-csv.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ExportCsvComponent<T> {
  private toastrService = inject(ToastrService);
  private dialogRef = inject(MatDialogRef<ExportCsvComponent<T>>);
  private readonly errorService = inject(ErrorService);

  tableColumns = signal<TableColumn<T>[]>([]);
  exportCSVDataFn = signal<(() => Observable<T[]>) | null>(null);
  tableColumnSelectors = signal<TableColumnSelector<T>[]>([]);
  tableColumnsEffect = effect(() => {
    this.tableColumnSelectors.set(
      this.tableColumns()
        .filter(column => !column.ignoreInExport)
        .map(tc => {
          return {
            tableColumn: tc,
            formControl: new FormControl<boolean>(false, { nonNullable: true }),
          };
        }),
    );
  });
  tableColumnFormControls = computed(() => this.tableColumnSelectors().map(tcs => tcs.formControl));

  loading = signal<boolean>(false);
  protected readonly ButtonType = ButtonType;

  exportClicked() {
    this.fetchCSVData();
  }

  cancelClicked() {
    this.dialogRef.close(false);
  }

  private fetchCSVData() {
    const fetchFn = this.exportCSVDataFn();
    if (fetchFn) {
      fetchFn()
        .pipe(indicateSignal(this.loading))
        .subscribe({
          next: data => {
            this.downloadCsvFile(data);
            this.toastrService.success(
              $localize`Check your downloads folder to access the file.`,
              $localize`Successfully Exported`,
            );
            this.dialogRef.close();
          },
          error: e => {
            this.errorService.handle(e, $localize`Error Exporting`);
          },
        });
    }
  }

  private downloadCsvFile(data: T[]) {
    const exportColumns = this.tableColumnSelectors()
      .filter(c => c.formControl.value)
      .map(c => c.tableColumn);
    const dataToExport = data.map(rowData => {
      const row: Record<string, string> = {};
      exportColumns.forEach(c => {
        row[c.title ?? ''] = c.value?.(rowData) ?? '';
      });
      return row;
    });

    CsvUtils.downloadFile(
      dataToExport,
      exportColumns.map(c => c.title ?? ''),
    );
  }
}

interface TableColumnSelector<T> {
  tableColumn: TableColumn<T>;
  formControl: FormControl<boolean>;
}
